import { PillarPage } from 'src/constants/searchPages'
import {
  IConfiguration,
  ILocation,
  LocationType,
  Maybe
} from 'src/types/graphql'
import { isAvailabilityClaimable } from 'src/utils/availabilityFreshness'
import { getDiscount } from 'src/utils/coverPhoto'
import type { SearchCardBadges } from 'src/utils/pixels/searchEvents'
import { getEffectiveDealType } from 'src/utils/pricing'

/** Card-level scarcity verdict. Returns the kind of urgency label to show
 * on a `<SearchCard>` based on the listing's `availability`, or null when
 * the card should stay quiet.
 *
 * Diverges from the listing-page `getActiveScarcityCount` util on purpose:
 *
 *  - **Excludes broker variants** (bodegas comerciales, naves industriales).
 *    Broker is a specialist-mediated B2B funnel — urgency theater is a
 *    mismatch. Mirrors the listing page where `variant.priceCard.showScarcity`
 *    is `false` for broker / fleet variants.
 *  - **Excludes parking.** Parking listings have N spots, not N units;
 *    scarcity reads as "X cajones disponibles" which lives in a different
 *    pill on the card and shouldn't double up.
 *  - **No watching-count case.** The listing-page util surfaces watching
 *    >1 as social proof. On a dense card grid that signal reads weak at
 *    realistic numbers (2-5 viewers) and risks looking inflated on a young
 *    marketplace. Scarcity is the only urgency signal cards expose.
 *  - **Only on a count we can stand behind** (`isAvailabilityClaimable`):
 *    SpotMe Now counts are platform-kept; off-platform ones need the
 *    server's `inventoryIsFresh` (host confirmation within 45 days).
 *  - **Defensive on missing data.** `getActiveScarcityCount` does
 *    `availability ?? 0` which trips `<= 3` and lights up "Quedan 0" when
 *    the fragment doesn't include the field. Here we require a real number. */
export const getCardScarcity = (
  config: IConfiguration,
  isBrokerMode: boolean,
  inventoryIsFresh: Maybe<boolean> | undefined
): { kind: 'last_one' | 'few_left'; count: number } | null => {
  if (isBrokerMode) return null
  if (config.locationType === LocationType.Parking) return null
  if (typeof config.availability !== 'number') return null
  if (!isAvailabilityClaimable(config, inventoryIsFresh)) return null
  const count = config.availability
  if (count === 1) return { kind: 'last_one', count }
  if (count >= 2 && count <= 3) return { kind: 'few_left', count }
  return null
}

/** Branch-level demand pill count for a card, or null. `recentInquiries` is
 * already null server-side below 3 distinct people / 30 days. Yields to a
 * scarcity pill so a card never stacks urgency on urgency — scarcity is the
 * sharper, unit-level signal. */
export const getCardDemand = (
  location: Pick<ILocation, 'recentInquiries'>,
  hasScarcity: boolean
): number | null =>
  !hasScarcity && typeof location.recentInquiries === 'number'
    ? location.recentInquiries
    : null

/** The badges a search/map card rendered for its `defaultConfiguration`, as
 * flat analytics props (see `SearchCardBadges`). Mirrors the card's own render
 * rules — `getDiscount` (SpotMe Now 50%), the server-gated `operatorPromo`
 * (null when hidden), `getCardScarcity` — so the event says what the renter
 * actually saw, not what the listing could show. */
export const buildCardBadges = (
  location: Pick<
    ILocation,
    | 'defaultConfiguration'
    | 'isVerified'
    | 'reviewScore'
    | 'inventoryIsFresh'
    | 'recentInquiries'
  >,
  isBrokerMode: boolean
): SearchCardBadges => {
  const config = location.defaultConfiguration
  const scarcity = config
    ? getCardScarcity(config, isBrokerMode, location.inventoryIsFresh)
    : null
  return {
    deal_type: config ? getEffectiveDealType(config) : undefined,
    badge_discount: Boolean(getDiscount(config, isBrokerMode)),
    badge_operator_promo: config?.operatorPromo?.kind ?? undefined,
    badge_scarcity: scarcity?.kind,
    badge_demand: getCardDemand(location, Boolean(scarcity)) !== null,
    badge_verified: Boolean(location.isVerified),
    review_score: location.reviewScore ?? undefined
  }
}

/** Bucketize the resolved total result count fired alongside
 * `search_completed`. Powers the PostHog supply-density diagnostic (search →
 * view_item conversion by inventory tier). Shared between the two fire sites:
 *
 *  - `SearchManager/index.tsx` — SSG initial-load fire (page mount)
 *  - `context/LocationsProvider.tsx` — dynamic re-fetch fire (filter / sort /
 *    map pan)
 *
 * Both sites MUST emit the same `results_count_bucket` shape so PostHog
 * cohorts and funnels can slice across the full search_completed population
 * without bucket-edge drift. */
export const bucketResultsCount = (n: number): string => {
  if (n <= 0) return '0'
  if (n <= 2) return '1-2'
  if (n <= 5) return '3-5'
  if (n <= 10) return '6-10'
  if (n <= 20) return '11-20'
  return '20+'
}

const PILLAR_SLUGS = new Set<string>(Object.values(PillarPage))

/** Pillar slug parsed from a path (defaults to the live pathname). Returns the
 * canonical `PillarPage` value when the first path segment is a known pillar,
 * else `undefined` (e.g. on `/search`).
 *
 * This is the reliable pillar signal for `search_completed`. `primary_location_type`
 * only ever logs the (usually unset) first `locationTypes` filter — it collapses
 * to `SELF_STORAGE_UNIT` and can't split the liquidity curve by pillar. A
 * result-derived vertical would be blank on zero-result searches, which are
 * exactly the searches we most need to attribute to a pillar (the supply gap).
 * The route is present regardless of result count, so it's the robust source.
 * Marketplace-liquidity dashboard slices on this — see
 * `docs/marketing/liquidity.md`. */
export const pillarFromPath = (pathname?: string): PillarPage | undefined => {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  const seg = path.split('/').filter(Boolean)[0]
  return seg && PILLAR_SLUGS.has(seg) ? (seg as PillarPage) : undefined
}

/** Pre-formatted distance bucket for card display. Returns `{ value, key }`
 * where `key` is the locale namespace tail (`DISTANCE_METERS` / `DISTANCE_KM`)
 * and `value` is the number to interpolate. Below 1 km we show meters
 * rounded to the nearest 50 m (privacy + readability), under 10 km one
 * decimal, otherwise integer kilometers. */
export const formatCardDistance = (
  km: number
): { key: 'DISTANCE_METERS' | 'DISTANCE_KM'; value: number } => {
  if (km < 1) {
    const meters = Math.max(50, Math.round((km * 1000) / 50) * 50)
    return { key: 'DISTANCE_METERS', value: meters }
  }
  if (km < 10) {
    return { key: 'DISTANCE_KM', value: Math.round(km * 10) / 10 }
  }
  return { key: 'DISTANCE_KM', value: Math.round(km) }
}

/**
 * The search ZONE a result was served in — the unit of analysis for the
 * directory dilution guardrail (plan §4.2).
 *
 * Deliberately the SEARCH context's city, not the listing's own municipality.
 * The directory tier floor is switched on per search zone, so the zone is the
 * unit the treatment is applied to; measuring by the listing's address would
 * mix treated and untreated impressions inside one bucket. It is also free —
 * the alternative needs `addressDetails` on every search card, which the
 * card fragment deliberately dropped for payload size.
 *
 * The value is the canonical city SLUG — the same shape `geoFields` puts on
 * `zone` in the add-to-cart family, so verified-listing CTR and checkout-start
 * rate slice on ONE dimension instead of needing a name↔slug mapping.
 *
 * Returns undefined for an unscoped map/geo search, which is a legitimate
 * bucket ("no zone"), not a gap to fill with a guess.
 */
export const resolveSearchZone = (
  routerCity: string | string[] | undefined,
  filterCity: string | null | undefined
): string | undefined => {
  const fromRoute = Array.isArray(routerCity) ? routerCity[0] : routerCity
  return fromRoute ?? filterCity ?? undefined
}
